"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Bug, Play, Search, Wrench } from "lucide-react";
import { WrenchMascot } from "./WrenchMascot";
import { WrenchCommandPalette } from "./WrenchCommandPalette";
import { WrenchWelcomeDialog } from "./WrenchWelcomeDialog";
import { WrenchIssueDialog } from "./WrenchIssueDialog";
import { formatShortcutLabel, useIsMacLike } from "@/lib/platformShortcuts";

const HIDDEN_KEY = "wrench-assistant-hidden";

/**
 * Routes where Wrench should stay hidden — sign-in flows, the embedded plugin
 * preview, and the texture editor (which already has its own focused chrome).
 */
const HIDDEN_PATH_PREFIXES = [
  "/auth/",
  "/plugin-preview",
  "/paintbooth-studio",
  "/locked",
];

function shouldHide(pathname: string | null): boolean {
  if (!pathname) return false;
  return HIDDEN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function WrenchAssistant() {
  const pathname = usePathname();
  const isMacLike = useIsMacLike();
  const shortcutLabel = formatShortcutLabel("⌘K", isMacLike);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [confirmHide, setConfirmHide] = useState(false);
  const launcherRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  // Sync `hidden` with localStorage + cross-tab/in-app visibility events.
  useEffect(() => {
    const read = () => {
      setHidden(localStorage.getItem(HIDDEN_KEY) === "1");
    };
    read();
    const onStorage = (e: StorageEvent) => {
      if (e.key === HIDDEN_KEY) read();
    };
    const onVisibilityChanged = () => read();
    window.addEventListener("storage", onStorage);
    window.addEventListener("wrench-visibility-changed", onVisibilityChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("wrench-visibility-changed", onVisibilityChanged);
    };
  }, []);

  // Reset the inline confirm row whenever the hover menu closes.
  useEffect(() => {
    if (!menuOpen) setConfirmHide(false);
  }, [menuOpen]);

  const hideForever = useCallback(() => {
    localStorage.setItem(HIDDEN_KEY, "1");
    window.dispatchEvent(new Event("wrench-visibility-changed"));
    setHidden(true);
    setMenuOpen(false);
    setConfirmHide(false);
  }, []);

  const showAgain = useCallback(() => {
    localStorage.removeItem(HIDDEN_KEY);
    window.dispatchEvent(new Event("wrench-visibility-changed"));
    setHidden(false);
  }, []);

  // No auto-trigger — the welcome dialog only opens when the user clicks
  // "Get Started" from the launcher hover menu. The mascot just sits in the
  // corner waiting on every page.

  // Global ⌘K / Ctrl+K to open the palette.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const isAccelerator = event.metaKey || event.ctrlKey;
      if (isAccelerator && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((current) => !current);
        return;
      }
      if (event.key === "Escape") {
        setPaletteOpen(false);
        setIssueOpen(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close the launcher menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (event: MouseEvent) => {
      const root = launcherRef.current;
      if (root && !root.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setMenuOpen(false), 180);
  }, [cancelClose]);

  // Welcome dialog is fully manual — closing it is just closing it.
  const dismissWelcome = useCallback(() => setWelcomeOpen(false), []);

  const openSearch = useCallback(() => {
    setMenuOpen(false);
    setPaletteOpen(true);
  }, []);

  const openWelcome = useCallback(() => {
    setMenuOpen(false);
    setWelcomeOpen(true);
  }, []);

  const openIssue = useCallback(() => {
    setMenuOpen(false);
    setIssueOpen(true);
  }, []);

  if (shouldHide(pathname)) return null;

  if (hidden) {
    return (
      <button
        type="button"
        onClick={showAgain}
        title="Show Wrench"
        aria-label="Show Wrench assistant"
        className="fixed bottom-3 right-3 z-[70] grid h-5 w-5 place-items-center rounded-full border border-border/40 bg-background/40 text-muted-foreground/60 opacity-50 backdrop-blur transition hover:opacity-100 hover:text-foreground"
      >
        <Wrench className="h-3 w-3" />
      </button>
    );
  }

  return (
    <>
      {/* Floating launcher (mascot + Issue button) */}
      <div className="pointer-events-none fixed bottom-5 right-5 z-[80] flex items-end gap-3">
        <div
          ref={launcherRef}
          className="pointer-events-auto relative"
          onMouseEnter={() => {
            cancelClose();
            setMenuOpen(true);
          }}
          onMouseLeave={scheduleClose}
        >
          {menuOpen ? (
            <div
              className="absolute bottom-full right-0 mb-3 w-[260px] origin-bottom-right rounded-2xl border border-border/70 bg-popover/95 p-2 shadow-[0_30px_60px_hsl(var(--background)/0.55)] backdrop-blur-xl"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <button
                type="button"
                onClick={openSearch}
                className="group flex w-full items-center gap-2.5 rounded-xl border border-border/70 bg-secondary/40 px-3 py-2 text-left text-xs text-muted-foreground transition hover:border-accent/40 hover:bg-accent/5 hover:text-foreground"
              >
                <Search className="h-3.5 w-3.5 text-muted-foreground transition group-hover:text-accent" />
                <span className="flex-1 truncate">Search pages, tools…</span>
                <span className="rounded-md border border-border/70 bg-secondary/70 px-1.5 py-0.5 font-mono text-[10px] text-foreground/80">
                  {shortcutLabel}
                </span>
              </button>
              <button
                type="button"
                onClick={openWelcome}
                className="mt-2 inline-flex w-full items-center gap-2 rounded-xl border border-primary/30 bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground shadow-[0_18px_50px_hsl(var(--primary)/0.22)] transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-[0_24px_70px_hsl(var(--primary)/0.28)]"
              >
                <span className="grid h-5 w-5 place-items-center rounded-full bg-primary-foreground/15">
                  <Play className="h-2.5 w-2.5 fill-current" />
                </span>
                Get Started
              </button>
              <button
                type="button"
                onClick={openIssue}
                className="group mt-2 flex w-full items-center gap-2.5 rounded-xl border border-border/70 bg-secondary/40 px-3 py-2 text-left text-xs text-muted-foreground transition hover:border-accent/40 hover:bg-accent/5 hover:text-foreground"
              >
                <Bug className="h-3.5 w-3.5 text-muted-foreground transition group-hover:text-accent" />
                <span className="flex-1 truncate">Report an issue</span>
              </button>
              {confirmHide ? (
                <div className="mt-2 rounded-md border border-border/60 bg-secondary/30 p-2 text-[11px] text-muted-foreground">
                  <p className="mb-1.5 leading-snug">Hide forever? A small button stays in the corner to bring it back.</p>
                  <div className="flex gap-1.5">
                    <button type="button" onClick={() => setConfirmHide(false)} className="flex-1 rounded-md border border-border/70 bg-background/60 px-2 py-1 text-foreground transition hover:bg-background">Cancel</button>
                    <button type="button" onClick={hideForever} className="flex-1 rounded-md border border-[hsl(var(--destructive)/0.4)] bg-[hsl(var(--destructive)/0.15)] px-2 py-1 font-semibold text-destructive transition hover:bg-[hsl(var(--destructive)/0.25)]">Hide</button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmHide(true)}
                  className="mt-2 inline-flex w-full items-center justify-center rounded-md px-2 py-1 text-[11px] text-muted-foreground/70 transition hover:bg-muted/40 hover:text-foreground"
                >
                  Hide Wrench
                </button>
              )}
              <p className="mt-2 px-1 text-[10px] leading-snug text-muted-foreground">
                Wrench will route you to the fastest path for what you&apos;re building.
              </p>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (menuOpen) {
                openSearch();
              } else {
                setMenuOpen(true);
              }
            }}
            onFocus={() => {
              cancelClose();
              setMenuOpen(true);
            }}
            onBlur={scheduleClose}
            aria-label={`Open Wrench (${shortcutLabel})`}
            aria-expanded={menuOpen}
            className="grid h-14 w-14 place-items-center rounded-full bg-transparent transition hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/70"
          >
            <WrenchMascot size={56} state={paletteOpen || welcomeOpen || menuOpen ? "talking" : "idle"} />
          </button>
        </div>
      </div>

      <WrenchCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <WrenchWelcomeDialog
        open={welcomeOpen}
        onClose={dismissWelcome}
        onSkip={dismissWelcome}
        onDoNotShowAgain={dismissWelcome}
      />
      <WrenchIssueDialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        contextPath={pathname ?? undefined}
      />
    </>
  );
}
